import React from "react";

import { LegacyMegaraxPricingRule, PricingRuleSheetRow } from "@megaron/crm-contracts";
import { DiffSyncPage, DiffSyncRecord } from "@megaron/diff-sync-webapp";
import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";

type PricingRuleDiffValue = Partial<PricingRuleSheetRow> & Record<string, unknown>;
type LegacyMegaraxPricingRuleDiffValue = Partial<LegacyMegaraxPricingRule> & Record<string, unknown>;
type PricingRulesSyncChannel = "sheetsPricingRule" | "legacyMegaraxPricingRule";

type Props<T extends Record<string, unknown>> = {
  title: string;
  channel: PricingRulesSyncChannel;
  directionLabels: {
    pull: string;
    push: string;
  };
  labels: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, (value: unknown) => React.ReactNode>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
};

export const PricingRulesSyncDiffPage = () => (
  <CrmPricingRulesSyncDiffPage<PricingRuleDiffValue>
    title="Synchronizacja Google Sheets - warunki handlowe"
    channel="sheetsPricingRule"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do google sheets",
    }}
    labels={pricingRuleLabels}
    formatters={pricingRuleFormatters}
    alwaysVisibleColumns={["customerId", "customerName", "priceList"]}
  />
);

export const LegacyMegaraxPricingRulesSyncDiffPage = () => (
  <CrmPricingRulesSyncDiffPage<LegacyMegaraxPricingRuleDiffValue>
    title="Migracja warunków handlowych z legacy Megarax"
    channel="legacyMegaraxPricingRule"
    directionLabels={{
      pull: "Do megarax.net",
      push: "Do legacy Megarax",
    }}
    labels={legacyMegaraxPricingRuleLabels}
    formatters={legacyMegaraxPricingRuleFormatters}
    alwaysVisibleColumns={["customerId", "customerName", "priceList"]}
  />
);

const CrmPricingRulesSyncDiffPage = <T extends Record<string, unknown>>(props: Props<T>) => {
  const client = useClientManager("crm");

  const pullQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPullable: true,
  });

  const pushQuery = client.diffQuery().useQuery({
    channel: props.channel,
    isPushable: true,
  });

  const allDiffsQuery = client.diffQuery().useQuery({
    channel: props.channel,
  });
  const channelQuery = client.diffChannelQuery().useQuery({
    channel: props.channel,
  });

  const refreshMutation = client.refreshDiffs().useMutation();
  const pullMutation = client.pullDiffs().useMutation();
  const pushMutation = client.pushDiffs().useMutation();

  const refetchDiffs = async () => {
    await Promise.all([pullQuery.refetch(), pushQuery.refetch(), allDiffsQuery.refetch()]);
  };

  const refresh = async () => {
    await refreshMutation.mutateAsync({ channel: props.channel });
    await refetchDiffs();
  };

  const pull = async (diffIds: string[]) => {
    await pullMutation.mutateAsync({ channel: props.channel, diffIds });
    await refetchDiffs();
  };

  const push = async (diffIds: string[]) => {
    await pushMutation.mutateAsync({ channel: props.channel, diffIds });
    await refetchDiffs();
  };

  return (
    <DiffSyncPage<T>
      title={props.title}
      pullableDiffs={pullQuery.data as DiffSyncRecord<T>[] | undefined}
      pushableDiffs={pushQuery.data as DiffSyncRecord<T>[] | undefined}
      errorDiffs={allDiffsQuery.data?.filter(isErrorDiff) as DiffSyncRecord<T>[] | undefined}
      directionLabels={props.directionLabels}
      labels={props.labels}
      formatters={props.formatters}
      alwaysVisibleColumns={props.alwaysVisibleColumns}
      remoteResourceUrl={channelQuery.data?.remoteResourceUrl}
      isLoading={
        pullQuery.isLoading ||
        pushQuery.isLoading ||
        allDiffsQuery.isLoading ||
        pullQuery.isFetching ||
        pushQuery.isFetching ||
        allDiffsQuery.isFetching
      }
      isRefreshing={refreshMutation.isLoading}
      isExecuting={pullMutation.isLoading || pushMutation.isLoading}
      onRefresh={refresh}
      onPull={pull}
      onPush={push}
    />
  );
};

const isErrorDiff = (diff: DiffSyncRecord<Record<string, unknown>>) => !!diff.error;

const pricingRuleLabels = {
  priceList: "Cennik",
  customerId: "Id klienta",
  customerName: "Klient",
  defaultDiscount: "Rabat domyślny",
  notes: "Notatki",
} satisfies Partial<Record<keyof PricingRuleSheetRow, string>>;

const pricingRuleFormatters = {
  defaultDiscount: (value) => (value === null || value === undefined ? "-" : value.toString()),
} satisfies Partial<Record<keyof PricingRuleSheetRow, (value: unknown) => React.ReactNode>>;

const legacyMegaraxPricingRuleLabels = {
  priceList: "Cennik",
  customerId: "Id klienta",
  customerName: "Klient",
  defaultDiscount: "Rabat domyślny",
  notes: "Notatki",
  modifiedAt: "Zmodyfikowano",
  modifiedBy: "Zmodyfikował",
} satisfies Partial<Record<keyof LegacyMegaraxPricingRule, string>>;

const legacyMegaraxPricingRuleFormatters = {
  defaultDiscount: (value) => (value === null || value === undefined ? "-" : value.toString()),
  modifiedAt: (value) => formatDateTimeValue(value),
} satisfies Partial<Record<keyof LegacyMegaraxPricingRule, (value: unknown) => React.ReactNode>>;

const formatDateTimeValue = (value: unknown) =>
  value instanceof Date ? Serializers.datetime.serialize(value) : value ? String(value) : "-";
